import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Users, Calendar, AlertTriangle, Trophy, Dumbbell, UserCheck, UserX, Hand, Shield, Megaphone, Trash2, Copy } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import AdminLayout from '../../components/AdminLayout';
import api from '../../utils/api';
import { toast } from 'sonner';
import { getLeadSource, getLeadSourceOptions } from '../../config/leadSources';

const AdminApplications = () => {
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [flowFilter, setFlowFilter] = useState('all');
  const [assignmentFilter, setAssignmentFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [claimingApp, setClaimingApp] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);
  const [dupLoading, setDupLoading] = useState(false);
  const [dupRecords, setDupRecords] = useState([]);
  const [dupCount, setDupCount] = useState(0);
  const [dupDeleting, setDupDeleting] = useState(false);

  useEffect(() => {
    loadApplications();
    loadCurrentUser();
  }, []);

  const loadCurrentUser = () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        // JWT stores user ID in 'sub' field
        setCurrentUser({ user_id: payload.sub });
      } catch (e) {
        console.error('Failed to parse token', e);
      }
    }
  };

  useEffect(() => {
    filterApplications();
  }, [applications, searchTerm, statusFilter, flowFilter, assignmentFilter, sourceFilter]);

  const loadApplications = async () => {
    try {
      const response = await api.get('/applications');
      setApplications(response.data);
    } catch (error) {
      console.error('Failed to load applications', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClaimApplication = async (e, appId) => {
    e.preventDefault();
    e.stopPropagation();
    setClaimingApp(appId);
    try {
      await api.post(`/applications/${appId}/claim`);
      toast.success('Application claimed successfully');
      loadApplications();
    } catch (error) {
      toast.error('Failed to claim application');
      console.error('Failed to claim application', error);
    } finally {
      setClaimingApp(null);
    }
  };

  const handleUnassignApplication = async (e, appId) => {
    e.preventDefault();
    e.stopPropagation();
    setClaimingApp(appId);
    try {
      await api.post(`/applications/${appId}/unassign`);
      toast.success('Application returned to queue');
      loadApplications();
    } catch (error) {
      toast.error('Failed to unassign application');
      console.error('Failed to unassign application', error);
    } finally {
      setClaimingApp(null);
    }
  };

  const handleDeleteApplication = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/applications/${deleteTarget.id}`);
      toast.success('Application deleted');
      setDeleteTarget(null);
      loadApplications();
    } catch (error) {
      toast.error('Failed to delete application');
    } finally {
      setDeleting(false);
    }
  };

  const openDuplicatePreview = async () => {
    setDupOpen(true);
    setDupLoading(true);
    try {
      const res = await api.get('/applications/duplicates/preview');
      setDupRecords(res.data.records || []);
      setDupCount(res.data.count || 0);
    } catch (error) {
      toast.error('Failed to load duplicate preview');
      setDupRecords([]);
      setDupCount(0);
    } finally {
      setDupLoading(false);
    }
  };

  const runDuplicateCleanup = async () => {
    setDupDeleting(true);
    try {
      const res = await api.post('/applications/duplicates/cleanup');
      toast.success(`Deleted ${res.data.deleted_count} duplicate application(s)`);
      setDupOpen(false);
      loadApplications();
    } catch (error) {
      toast.error('Failed to delete duplicates');
    } finally {
      setDupDeleting(false);
    }
  };

  const filterApplications = () => {
    let filtered = applications;
    if (statusFilter !== 'all') {
      if (statusFilter === 'closed_won') {
        filtered = filtered.filter(app => ['closed_won', 'accepted', 'bound'].includes(app.status));
      } else if (statusFilter === 'closed_lost') {
        filtered = filtered.filter(app => ['closed_lost', 'declined'].includes(app.status));
      } else {
        filtered = filtered.filter(app => app.status === statusFilter);
      }
    }

    if (flowFilter !== 'all') {
      filtered = filtered.filter(app => (app.flow_template || 'AMATEUR_SPORTS_V1') === flowFilter);
    }

    if (assignmentFilter !== 'all') {
      if (assignmentFilter === 'unassigned') {
        filtered = filtered.filter(app => !app.assigned_to_id);
      } else if (assignmentFilter === 'assigned') {
        filtered = filtered.filter(app => app.assigned_to_id);
      } else if (assignmentFilter === 'mine') {
        filtered = filtered.filter(app => app.assigned_to_id === currentUser?.user_id);
      }
    }

    if (sourceFilter !== 'all') {
      filtered = filtered.filter(app => getLeadSource(app.utm_source, app.utm_medium, app.utm_campaign) === sourceFilter);
    }

    if (searchTerm) {
      filtered = filtered.filter(app => 
        app.org_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        getApplicantName(app).toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.affiliate_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.applicant_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.assigned_to_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredApplications(filtered);
  };

  // Helper function to get applicant name (handles both old and new schema)
  const getApplicantName = (app) => {
    if (app.first_name && app.last_name) {
      return `${app.first_name} ${app.last_name}`;
    }
    return app.applicant_name || 'N/A';
  };

  // Helper function to get participant count
  const getParticipantCount = (app) => {
    // For fitness flow, show active members instead
    if (app.flow_template === 'BOUTIQUE_FITNESS_V1') {
      return app.active_members || 'N/A';
    }
    return app.num_participants_under_19 || app.num_participants || 'N/A';
  };

  // Helper to get participant label
  const getParticipantLabel = (app) => {
    if (app.flow_template === 'BOUTIQUE_FITNESS_V1') {
      return 'members';
    }
    return 'participants';
  };

  // Helper function to check claims history
  const hasClaimsHistory = (app) => {
    return app.claims_history_flag || app.has_claims_history || false;
  };

  // Helper to get flow badge
  const getFlowBadge = (app) => {
    const flow = app.flow_template || 'AMATEUR_SPORTS_V1';
    if (flow === 'BOUTIQUE_FITNESS_V1') {
      return (
        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[#ECFDF5] text-[#065F46] border border-[#A7F3D0]">
          <Dumbbell className="h-3 w-3" />
          Fitness
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[#EEF0FF] text-[#6B5CE7] border border-[#C4BFFF]">
        <Trophy className="h-3 w-3" />
        Sports
      </span>
    );
  };

  // Lead-source (UTM) channel badge
  const getSourceBadge = (app) => {
    const label = getLeadSource(app.utm_source, app.utm_medium, app.utm_campaign);
    return (
      <span
        data-testid={`source-badge-${app.id}`}
        className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[#FFF7ED] text-[#9A3412] border border-[#FED7AA]"
      >
        <Megaphone className="h-3 w-3" />
        {label}
      </span>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#7B6FE8]"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-black text-[#1A1A1A] uppercase tracking-wide" style={{fontFamily: "Barlow Condensed, sans-serif"}}>Applications</h1>
            <p className="text-[#6B7280] mt-1">Manage insurance applications and quotes</p>
          </div>
          <Button
            variant="outline"
            data-testid="dedupe-open-btn"
            onClick={openDuplicatePreview}
            className="border-[#E5E7EB] text-[#1A1A1A] hover:bg-[#F3F4F6]"
          >
            <Copy className="h-4 w-4 mr-2" />
            Clean Up Duplicates
          </Button>
        </div>

        {/* Filters */}
        <div className="flex gap-4 flex-wrap">
          <div className="flex-1 relative min-w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#9CA3AF]" />
            <Input
              data-testid="search-applications-input"
              placeholder="Search by organization, applicant, email, affiliate, or assignee..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-11 border-[#E5E7EB] focus:border-[#7B6FE8] focus:ring-[#7B6FE8]"
            />
          </div>
          <Select value={assignmentFilter} onValueChange={setAssignmentFilter}>
            <SelectTrigger data-testid="assignment-filter-select" className="w-48 h-11 border-[#E5E7EB]">
              <SelectValue placeholder="All Assignments" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Assignments</SelectItem>
              <SelectItem value="unassigned">
                <span className="flex items-center gap-2">
                  <UserX className="h-4 w-4 text-[#9CA3AF]" />
                  Unassigned
                </span>
              </SelectItem>
              <SelectItem value="mine">
                <span className="flex items-center gap-2">
                  <Hand className="h-4 w-4 text-[#7B6FE8]" />
                  My Applications
                </span>
              </SelectItem>
              <SelectItem value="assigned">
                <span className="flex items-center gap-2">
                  <UserCheck className="h-4 w-4 text-[#065F46]" />
                  Assigned
                </span>
              </SelectItem>
            </SelectContent>
          </Select>
          <Select value={flowFilter} onValueChange={setFlowFilter}>
            <SelectTrigger data-testid="flow-filter-select" className="w-48 h-11 border-[#E5E7EB]">
              <SelectValue placeholder="All Flows" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Flow Types</SelectItem>
              <SelectItem value="AMATEUR_SPORTS_V1">
                <span className="flex items-center gap-2">
                  <Trophy className="h-4 w-4 text-[#7B6FE8]" />
                  Sports
                </span>
              </SelectItem>
              <SelectItem value="BOUTIQUE_FITNESS_V1">
                <span className="flex items-center gap-2">
                  <Dumbbell className="h-4 w-4 text-[#065F46]" />
                  Fitness
                </span>
              </SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="status-filter-select" className="w-48 h-11 border-[#E5E7EB]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="submitted">Submitted</SelectItem>
              <SelectItem value="quoted">Quoted</SelectItem>
              <SelectItem value="qualified_opp">Qualified Opp</SelectItem>
              <SelectItem value="closed_won">Closed Won</SelectItem>
              <SelectItem value="closed_lost">Closed Lost</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger data-testid="source-filter-select" className="w-52 h-11 border-[#E5E7EB]">
              <SelectValue placeholder="All Sources" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sources</SelectItem>
              {getLeadSourceOptions().map((label) => (
                <SelectItem key={label} value={label}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Applications List */}
        <Card className="bg-white border border-[#E5E7EB] shadow-none">
          <CardHeader className="border-b border-[#E5E7EB] bg-[#F7F8FA]">
            <CardTitle className="text-base font-semibold text-[#1A1A1A]">{filteredApplications.length} Applications</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {filteredApplications.length === 0 ? (
              <p className="text-[#6B7280] text-center py-8">No applications found</p>
            ) : (
              <div className="divide-y divide-[#E5E7EB]">
                {filteredApplications.map((app) => (
                  <Link
                    key={app.id}
                    to={`/admin/applications/${app.id}`}
                    data-testid={`application-row-${app.id}`}
                    className="block p-4 hover:bg-[#F3F4F6] transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        {/* Organization Name & Status */}
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          <h4 className="font-semibold text-[#1A1A1A] text-base">{app.org_name}</h4>
                          {getFlowBadge(app)}
                          {getSourceBadge(app)}
                          <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                            app.status === 'submitted' ? 'bg-[#FFFBEB] border-[#FDE68A] text-[#92400E]' :
                            app.status === 'quoted' ? 'bg-[#EEF0FF] border-[#C4BFFF] text-[#6B5CE7]' :
                            app.status === 'qualified_opp' ? 'bg-[#F0FDF4] border-[#86EFAC] text-[#166534]' :
                            app.status === 'accepted' || app.status === 'closed_won' || app.status === 'bound' ? 'bg-[#ECFDF5] border-[#A7F3D0] text-[#065F46]' :
                            app.status === 'closed_lost' ? 'bg-[#FEF2F2] border-[#FECACA] text-[#991B1B]' :
                            'bg-[#F3F4F6] border-[#E5E7EB] text-[#374151]'
                          }`}>
                            {app.status === 'qualified_opp' ? 'Qualified Opp' :
                             app.status === 'closed_won' || app.status === 'accepted' || app.status === 'bound' ? 'Closed Won' :
                             app.status === 'closed_lost' ? 'Closed Lost' :
                             app.status}
                          </span>
                          {hasClaimsHistory(app) && (
                            <Badge variant="outline" className="bg-[#FFFBEB] text-[#92400E] border-[#FDE68A]">
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              Claims
                            </Badge>
                          )}
                          {app.risk_assessment_id && (
                            <Badge variant="outline" className="bg-[#EEF0FF] text-[#6B5CE7] border-[#C4BFFF]" data-testid={`ra-badge-${app.id}`}>
                              <Shield className="h-3 w-3 mr-1" />
                              From Assessment
                            </Badge>
                          )}
                        </div>

                        {/* Applicant Info */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-x-6 gap-y-2 text-sm">
                          <div>
                            <span className="text-[#6B7280]">Applicant:</span>{' '}
                            <span className="text-[#1A1A1A] font-medium">{getApplicantName(app)}</span>
                          </div>
                          <div>
                            <span className="text-[#6B7280]">Email:</span>{' '}
                            <span className="text-[#1A1A1A]">{app.applicant_email}</span>
                          </div>
                          <div>
                            <span className="text-[#6B7280]">Phone:</span>{' '}
                            <span className="text-[#1A1A1A]">{app.applicant_phone}</span>
                          </div>
                          <div>
                            <span className="text-[#6B7280]">Affiliate:</span>{' '}
                            <span className="text-[#1A1A1A]">{app.affiliate_name}</span>
                          </div>
                        </div>

                        {/* Coverage Details */}
                        <div className="flex items-center gap-3 mt-3 text-sm flex-wrap">
                          <div className="flex items-center gap-1.5 text-[#6B7280] bg-[#F3F4F6] px-2.5 py-1 rounded-full border border-[#E5E7EB]">
                            <Users className="h-3.5 w-3.5" />
                            <span>{getParticipantCount(app)} {getParticipantLabel(app)}</span>
                          </div>
                          <div className="flex items-center gap-1.5 text-[#6B7280] bg-[#F3F4F6] px-2.5 py-1 rounded-full border border-[#E5E7EB]">
                            <Calendar className="h-3.5 w-3.5" />
                            <span>Start: {app.policy_effective_date || app.coverage_start_date || 'N/A'}</span>
                          </div>
                          {(app.gl_limit || app.gl_limit_per_occurrence) && (
                            <div className="text-[#6B5CE7] bg-[#EEF0FF] px-2.5 py-1 rounded-full border border-[#C4BFFF]">
                              GL: {app.gl_limit || app.gl_limit_per_occurrence}
                            </div>
                          )}
                          {/* Assignment Badge */}
                          {app.assigned_to_id ? (
                            <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border ${
                              app.assigned_to_id === currentUser?.user_id 
                                ? 'bg-[#EEF0FF] border-[#C4BFFF] text-[#6B5CE7]' 
                                : 'bg-[#ECFDF5] border-[#A7F3D0] text-[#065F46]'
                            }`}>
                              <UserCheck className="h-3.5 w-3.5" />
                              <span>{app.assigned_to_id === currentUser?.user_id ? 'Assigned to Me' : app.assigned_to_name}</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5 text-[#9CA3AF] bg-[#F7F8FA] px-2.5 py-1 rounded-full border border-[#E5E7EB]">
                              <UserX className="h-3.5 w-3.5" />
                              <span>Unassigned</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Right side: Date + Actions */}
                      <div className="flex flex-col items-end gap-2 shrink-0">
                        <div className="text-right text-sm text-[#6B7280]">
                          <div>{app.submitted_at ? new Date(app.submitted_at).toLocaleDateString() : 'N/A'}</div>
                          <div className="text-xs text-[#9CA3AF] mt-1">
                            {app.submitted_at ? new Date(app.submitted_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : ''}
                          </div>
                        </div>
                        {/* Claim/Unassign Button */}
                        {!app.assigned_to_id ? (
                          <Button
                            size="sm"
                            onClick={(e) => handleClaimApplication(e, app.id)}
                            disabled={claimingApp === app.id}
                            data-testid={`claim-btn-${app.id}`}
                            className="bg-[#7B6FE8] text-white hover:bg-[#6B5CE7] text-xs h-8"
                          >
                            <Hand className="h-3 w-3 mr-1" />
                            {claimingApp === app.id ? 'Claiming...' : 'Claim'}
                          </Button>
                        ) : app.assigned_to_id === currentUser?.user_id ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => handleUnassignApplication(e, app.id)}
                            disabled={claimingApp === app.id}
                            data-testid={`unassign-btn-${app.id}`}
                            className="border-[#E5E7EB] text-[#6B7280] hover:bg-[#F3F4F6] text-xs h-8"
                          >
                            <UserX className="h-3 w-3 mr-1" />
                            {claimingApp === app.id ? 'Releasing...' : 'Release'}
                          </Button>
                        ) : null}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDeleteTarget(app); }}
                          data-testid={`delete-btn-${app.id}`}
                          className="text-[#991B1B] hover:bg-[#FEF2F2] text-xs h-8"
                          title="Delete application"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Delete confirmation */}
        <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
          <AlertDialogContent data-testid="delete-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this application?</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this application{deleteTarget ? ` for "${deleteTarget.org_name}"` : ''}? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
              <AlertDialogAction
                data-testid="delete-confirm-btn"
                onClick={handleDeleteApplication}
                className="bg-[#991B1B] text-white hover:bg-[#7F1D1D]"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Duplicate cleanup preview */}
        <Dialog open={dupOpen} onOpenChange={setDupOpen}>
          <DialogContent className="max-w-3xl" data-testid="dedupe-dialog">
            <DialogHeader>
              <DialogTitle>Duplicate Cleanup Preview</DialogTitle>
              <DialogDescription>
                Duplicates = same applicant email AND organization. Records marked "Closed Won" are never removed. For the rest, the most recent submission is kept and the older ones below are proposed for removal — review before deleting.
              </DialogDescription>
            </DialogHeader>
            {dupLoading ? (
              <div className="py-10 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#7B6FE8]"></div></div>
            ) : dupCount === 0 ? (
              <p className="py-8 text-center text-[#6B7280]" data-testid="dedupe-empty">No duplicate applications found. Nothing to remove.</p>
            ) : (
              <>
                <div className="max-h-[50vh] overflow-y-auto border border-[#E5E7EB] rounded-lg">
                  <table className="w-full text-sm" data-testid="dedupe-table">
                    <thead className="bg-[#F7F8FA] text-[#6B7280] text-xs uppercase sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2 font-semibold">Organization</th>
                        <th className="text-left px-3 py-2 font-semibold">Email</th>
                        <th className="text-left px-3 py-2 font-semibold">Submitted</th>
                        <th className="text-left px-3 py-2 font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E5E7EB]">
                      {dupRecords.map((r) => (
                        <tr key={r.application_id} data-testid={`dedupe-row-${r.application_id}`}>
                          <td className="px-3 py-2 text-[#1A1A1A]">{r.org_name}</td>
                          <td className="px-3 py-2 text-[#6B7280]">{r.applicant_email}</td>
                          <td className="px-3 py-2 text-[#6B7280]">{r.submitted_at ? new Date(r.submitted_at).toLocaleDateString() : 'N/A'}</td>
                          <td className="px-3 py-2 text-[#6B7280]">{r.status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <DialogFooter className="mt-2 items-center">
                  <p className="text-xs text-[#991B1B] mr-auto self-center">{dupCount} record{dupCount === 1 ? '' : 's'} will be permanently deleted. This cannot be undone.</p>
                  <Button variant="outline" onClick={() => setDupOpen(false)} className="border-[#E5E7EB]">Cancel</Button>
                  <Button
                    data-testid="dedupe-delete-btn"
                    onClick={runDuplicateCleanup}
                    disabled={dupDeleting}
                    className="bg-[#991B1B] text-white hover:bg-[#7F1D1D]"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {dupDeleting ? 'Deleting...' : `Delete ${dupCount} Duplicate${dupCount === 1 ? '' : 's'}`}
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminApplications;
